import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { MdOutlineMemory, MdWavingHand, MdRefresh } from "react-icons/md";
import { useParams } from 'react-router-dom';
import { WiHumidity } from "react-icons/wi";

import ApiMServer from "../../services/ApiMServer";
import ApiGServer from "../../services/ApiGServer";

import Reload from "../../components/reload/reload";
import Grafico from "./components/Graficos/Graficos";
import ProgressBar from "./components/ProgressBar/ProgressBar";
import "./AccessId.css"; // Importando o CSS
import { formatLocal } from '../../utilis/dateUtils';
import GraficosFard from "./components/Graficos/GraficosFard";

const AccessId = () => {
  const { id } = useParams();

  const [companyLocal, setCompanyLocal] = useState(() => JSON.parse(localStorage.getItem('company')) || {});
  const [equipmentsLocal, setEquipmentsLocal] = useState(() => JSON.parse(localStorage.getItem('equipments')) || []);

  const [isLoading, setIsLoading] = useState(true);
  const [reload, setReload] = useState(false);
  const prevCompanyRef = useRef(null);
  // useRef evita stale closure no setInterval — useState captura o valor inicial
  const isRefreshingRef = useRef(false);
  const [progressReset, setProgressReset] = useState(0);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Mapa: serial SEMT → dados do alarme vinculado
  const alarmMap = useMemo(() => {
    const map = {};
    const otherEquipments = Array.isArray(companyLocal?.otherEquipments) ? companyLocal.otherEquipments : [];
    otherEquipments
      .filter(e => e.serialNumber?.toUpperCase().includes('ALRM'))
      .forEach(alarm => {
        if (Array.isArray(alarm.equipments)) {
          alarm.equipments.forEach(semtSerial => {
            map[semtSerial] = {
              alarmSerial: alarm.serialNumber,
              active: alarm.active,
              minHumidity: alarm.minHumidity,
              maxHumidity: alarm.maxHumidity,
              description: alarm.description,
            };
          });
        }
      });
    return map;
  }, [companyLocal]);

  // Mapa: serial SEMT → dados do rodízio vinculado
  const rotationMap = useMemo(() => {
    const map = {};
    const otherEquipments = Array.isArray(companyLocal?.otherEquipments) ? companyLocal.otherEquipments : [];
    otherEquipments
      .filter(e => e.serialNumber?.toUpperCase().includes('RODZ'))
      .forEach(rotation => {
        if (Array.isArray(rotation.equipments)) {
          rotation.equipments.forEach(semtSerial => {
            map[semtSerial] = {
              rotationSerial: rotation.serialNumber,
              description: rotation.description,
              rotationOn: String(rotation.rotationStatus).toUpperCase() === 'ON',
            };
          });
        }
      });
    return map;
  }, [companyLocal]);

  const handleCompany = useCallback(async () => {
    if (isRefreshingRef.current) return;

    let success = false;
    try {
      isRefreshingRef.current = true;
      let { data } = await ApiMServer.post(`/company/detail`, { id });
      const newDataString = JSON.stringify(data || {});
      if (prevCompanyRef.current !== newDataString) {
        prevCompanyRef.current = newDataString;
        localStorage.setItem('company', newDataString);
        setCompanyLocal(data || {});
        if (data?.equipments?.length) {
          let equip = await ApiGServer.post(`equipment/filter`, { filter: data?.equipments });
          localStorage.setItem('equipments', JSON.stringify(equip.data));
          setEquipmentsLocal(equip.data || []);
        }
        success = true;
      }
    } catch (error) {
      console.error('Erro ao buscar dados:', error?.message);
    } finally {
      setIsLoading(false);
      isRefreshingRef.current = false;
      if (success) setReload(prev => (typeof prev === 'number' ? prev + 1 : 1));
    }
  }, [id]);

  const handleForceRefresh = useCallback(() => {
    setProgressReset(prev => prev + 1);
    handleCompany();
  }, [handleCompany]);

  const onProgressFinish = useCallback(async () => {
    await handleCompany();
    setProgressReset(prev => prev + 1);
  }, [handleCompany]);

  // chamar apenas no mount: manter comportamento original (handleCompany gerencia refreshs subsequentes).
  useEffect(() => {
    handleCompany();

    const timeInterval = setInterval(() => setCurrentTime(new Date()), 60000);
    const dataRefreshInterval = setInterval(() => handleCompany(), 120000);

    return () => {
      clearInterval(timeInterval);
      clearInterval(dataRefreshInterval);
    };
  }, [handleCompany]);

  const formatDate = () => {
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    // formatar explicitamente para o padrão brasileiro
    return formatLocal(currentTime, 'pt-BR', options);
  }

  return (
    <div style={{
      background: "linear-gradient(135deg, #121212 0%, #1e1e1e 100%)",
      minHeight: "100vh",
      padding: "1rem 0",
      color: "#e0e0e0"
    }}>

      <Container fluid className="px-2">

        {isLoading && <Reload />}

        <Card className="mb-3 shadow border-0 dark-card" style={{ borderRadius: "16px", overflow: "hidden", backgroundColor: "#282c34" }}>
          <Card.Body className="p-2">
            <Row className="align-items-center">
              <Col lg={6} md={12} className="mb-md-3 mb-lg-0">
                <h2 className="dashboard-title mb-0">
                  <MdWavingHand className="icon-wave" />
                  <span>Monitoramento em Tempo Real</span>
                </h2>
              </Col>
              <Col lg={6} md={12} className="text-lg-end text-center">
                <div className="d-flex justify-content-lg-end justify-content-center align-items-center gap-3 flex-wrap">
                  <span
                    className="dashboard-date"
                    onClick={handleForceRefresh}
                  >
                    {formatDate()}
                  </span>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <ProgressBar onFinish={onProgressFinish} reset={progressReset} />

        <div className="dashboard-stats mb-3">
          <Row className="g-3">
            <Col lg={6} md={6} sm={12}>
              <Card className="dashboard-stat-card h-100">
                <Card.Body className="d-flex align-items-center p-2">
                  <div className="stat-icon">
                    <MdOutlineMemory />
                  </div>
                  <div className="stat-info">
                    <h3 className="dark-text">{equipmentsLocal?.length || 0}</h3>
                    <p className="dark-text-secondary">Equipamentos Conectados</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={6} md={6} sm={12}>
              <Card className="dashboard-stat-card h-100">
                <Card.Body className="d-flex align-items-center p-2">
                  <div className="stat-icon">
                    <WiHumidity />
                  </div>
                  <div className="stat-info">
                    <h3 className="dark-text">{companyLocal?.name || "Empresa"}</h3>
                    <p className="dark-text-secondary">Monitoramento Ativo</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        <Row className="equipment-grids g-3">

          {/* {equipmentsLocal?.length > 0 && companyLocal?.equipments?.map((serial) => (
            <Grafico
              key={serial}
              company={companyLocal}
              serial={serial}
              reload={reload}
              detailsEquip={equipmentsLocal}
              progressReset={progressReset}
            />
          ))} */}

          {equipmentsLocal?.length > 0 && (
            <>
              {/* Primeiro renderiza os FARD */}
              {companyLocal?.equipments
                ?.filter(serial => serial.includes("FARD"))
                .map((serial) => (
                  <GraficosFard
                    key={serial}
                    company={companyLocal}
                    serial={serial}
                    reload={reload}
                    detailsEquip={equipmentsLocal}
                    progressReset={progressReset}
                    alarmInfo={alarmMap[serial] || null}
                    rotationInfo={rotationMap[serial] || null}
                  />
                ))}

              {/* Depois renderiza os demais */}
              {companyLocal?.equipments
                ?.filter(serial => !serial.includes("FARD"))
                .map((serial) => (
                  <Grafico
                    key={serial}
                    company={companyLocal}
                    serial={serial}
                    reload={reload}
                    detailsEquip={equipmentsLocal}
                    progressReset={progressReset}
                    alarmInfo={alarmMap[serial] || null}
                    rotationInfo={rotationMap[serial] || null}
                  />
                ))}
            </>
          )}



          {(!equipmentsLocal || equipmentsLocal.length === 0) && !isLoading && (
            <Col xs={12}>
              <Card className="text-center p-5 border-0 shadow-sm dark-card" style={{ borderRadius: "16px" }}>
                <Card.Body>
                  <h4 className="dark-text">Nenhum equipamento encontrado</h4>
                  <p className="text-muted dark-text-secondary">Não há equipamentos configurados para monitoramento.</p>
                  <Button
                    className="refresh-button mt-3"
                    onClick={handleForceRefresh}
                  >
                    Verificar Novamente
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          )}
        </Row>
      </Container>
    </div>
  );
};

export default AccessId;
